/**
 * HTML Sanitization Utility
 *
 * Uses DOMPurify to sanitize HTML content and prevent XSS attacks.
 * This utility should be used with any dangerouslySetInnerHTML usage.
 */
import DOMPurify from 'dompurify';

/**
 * Default sanitization configuration
 * Allows common formatting tags while blocking dangerous elements
 */
const DEFAULT_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    // Text formatting
    'p', 'br', 'b', 'i', 'u', 'strong', 'em', 's', 'strike', 'sub', 'sup', 'cite',
    // Headers
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    // Lists
    'ul', 'ol', 'li',
    // Links and media
    'a', 'img',
    // Blocks
    'blockquote', 'pre', 'code', 'hr',
    // Tables
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
    // Layout
    'div', 'span',
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'class', 'style', 'target', 'rel',
    'width', 'height', 'colspan', 'rowspan', 'scope',
  ],
  ALLOW_DATA_ATTR: false,
  // Force all links to open safely
  ADD_ATTR: ['target', 'rel'],
};

/**
 * Strict configuration - minimal tags for plain text with basic formatting
 */
const STRICT_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em'],
  ALLOWED_ATTR: [],
  ALLOW_DATA_ATTR: false,
};

/**
 * Sanitize HTML content using default configuration
 * @param dirty - Untrusted HTML string
 * @returns Sanitized HTML string safe for rendering
 */
export const sanitizeHtml = (dirty: string | undefined | null): string => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, DEFAULT_CONFIG);
};

/**
 * Sanitize HTML content using strict configuration (minimal tags)
 * @param dirty - Untrusted HTML string
 * @returns Sanitized HTML string with only basic formatting
 */
export const sanitizeHtmlStrict = (dirty: string | undefined | null): string => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, STRICT_CONFIG);
};

/**
 * Sanitize HTML content with custom configuration
 * @param dirty - Untrusted HTML string
 * @param config - Custom DOMPurify configuration
 * @returns Sanitized HTML string
 */
export const sanitizeHtmlCustom = (
  dirty: string | undefined | null,
  config: DOMPurify.Config
): string => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, config);
};

const NAMED_HTML_ENTITIES: Readonly<Record<string, string>> = {
  nbsp: ' ',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  '#39': "'",
};

/** Decode HTML entities in a single pass without DOM reinterpretation. */
const decodeHtmlEntities = (text: string): string => {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, entity: string) => {
    const lower = entity.toLowerCase();
    if (lower === 'amp') {
      return '&';
    }
    if (lower.startsWith('#x')) {
      const code = parseInt(lower.slice(2), 16);
      if (Number.isNaN(code) || code < 0 || code > 0x10ffff) {
        return match;
      }
      return String.fromCodePoint(code);
    }
    if (lower.startsWith('#')) {
      const code = parseInt(lower.slice(1), 10);
      if (Number.isNaN(code) || code < 0 || code > 0x10ffff) {
        return match;
      }
      return String.fromCodePoint(code);
    }
    return NAMED_HTML_ENTITIES[lower] ?? match;
  });
};

/**
 * Strip all HTML tags and return plain text with decoded entities
 * @param dirty - HTML string
 * @returns Plain text without any HTML, with entities decoded
 */
export const stripHtml = (dirty: string | undefined | null): string => {
  if (!dirty) return '';
  const decoded = decodeHtmlEntities(dirty);
  return DOMPurify.sanitize(decoded, { ALLOWED_TAGS: [] }).trim();
};

/** Check whether HTML/rich-text input contains non-whitespace text content. */
export const hasActualTextContent = (html: string | undefined | null): boolean => {
  return stripHtml(html).length > 0;
};

export default sanitizeHtml;

const NOTE_FORMATTING_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'p', 'br', 'b', 'i', 'u', 'strong', 'em', 's', 'strike', 'sub', 'sup',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'blockquote', 'hr',
    'pre', 'code',
    'img', 'a',
  ],
  ALLOWED_ATTR: ['class', 'src', 'alt', 'width', 'height', 'href', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
  ADD_DATA_URI_TAGS: ['img'],
  ADD_ATTR: ['target', 'rel'],
};

const NOTE_PRE_CODE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['pre', 'code'],
  ALLOWED_ATTR: ['class'],
  ALLOW_DATA_ATTR: false,
};

type NoteHtmlSegment = { type: 'content' | 'pre'; value: string };

/** Whether `<` at index starts an HTML-like tag (not math/compare). */
const looksLikeHtmlTag = (text: string, start: number): boolean => {
  if (text[start] !== '<') return false;
  const next = text[start + 1];
  if (!next) return false;
  return next === '/' || (next >= 'a' && next <= 'z') || (next >= 'A' && next <= 'Z');
};

/** Remove raw `<tag ...>` sequences from plain text (outside code blocks). */
const stripRawAngleBracketMarkup = (text: string): string => {
  let result = '';
  let i = 0;
  while (i < text.length) {
    if (looksLikeHtmlTag(text, i)) {
      const close = text.indexOf('>', i);
      if (close === -1) {
        result += text[i];
        i++;
        continue;
      }
      i = close + 1;
      continue;
    }
    result += text[i];
    i++;
  }
  return result;
};

/** Split HTML into alternating normal content and TipTap code-block segments. */
const segmentNoteHtml = (html: string): NoteHtmlSegment[] => {
  const segments: NoteHtmlSegment[] = [];
  let i = 0;
  const lower = html.toLowerCase();

  while (i < html.length) {
    const preStart = lower.indexOf('<pre', i);
    if (preStart === -1) {
      const tail = html.slice(i);
      if (tail) {
        segments.push({ type: 'content', value: tail });
      }
      break;
    }

    if (preStart > i) {
      segments.push({ type: 'content', value: html.slice(i, preStart) });
    }

    const closeTag = '</pre>';
    const closeStart = lower.indexOf(closeTag, preStart);
    if (closeStart === -1) {
      segments.push({ type: 'content', value: html.slice(preStart) });
      break;
    }

    segments.push({ type: 'pre', value: html.slice(preStart, closeStart + closeTag.length) });
    i = closeStart + closeTag.length;
  }

  return segments;
};

/** Strip literal pasted HTML from text nodes while keeping editor formatting tags. */
const stripLiteralHtmlFromTextNodes = (root: ParentNode, insidePre = false): void => {
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (!insidePre && node.textContent) {
        const cleaned = stripRawAngleBracketMarkup(decodeHtmlEntities(node.textContent));
        if (cleaned !== node.textContent) {
          node.textContent = cleaned;
        }
      }
      continue;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      stripLiteralHtmlFromTextNodes(el, insidePre || el.tagName.toLowerCase() === 'pre');
    }
  }
};

const sanitizeNoteContentSegment = (segment: string): string => {
  if (!segment.trim()) {
    return '';
  }

  const sanitizedBody = DOMPurify.sanitize(segment, NOTE_FORMATTING_CONFIG);
  if (!sanitizedBody.trim()) {
    return '';
  }

  const doc = new DOMParser().parseFromString(`<div>${sanitizedBody}</div>`, 'text/html');
  stripLiteralHtmlFromTextNodes(doc.body.firstElementChild ?? doc.body);
  return (doc.body.firstElementChild ?? doc.body).innerHTML;
};

const sanitizeNotePreSegment = (segment: string): string => {
  return DOMPurify.sanitize(segment, NOTE_PRE_CODE_CONFIG);
};

/**
 * Sanitize note/work-note HTML before persistence.
 * - Allows editor formatting (bold, lists, headings, etc.)
 * - Preserves intentional code blocks (`</>` toolbar) with full inner HTML text
 * - Strips raw HTML pasted as plain text (e.g. `<label className=...>`)
 */
export const sanitizeNoteInput = (text: string | undefined | null): string => {
  if (!text) return '';

  const segments = segmentNoteHtml(text);
  if (segments.length === 0) {
    return '';
  }

  return segments
    .map((segment) => (
      segment.type === 'pre'
        ? sanitizeNotePreSegment(segment.value)
        : sanitizeNoteContentSegment(segment.value)
    ))
    .join('')
    .trim();
};
